import { ImportIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { read, utils, WorkBook } from 'xlsx';
import { XlsxFileImport } from '../types/XlsxImportTypes';
import styles from './XlsxImportDropper.module.scss';

type XlsxImportDropperProps = {
  onLoad: (loading: boolean) => void;
  onFileLoaded: (fileImport: XlsxFileImport) => void;
  className?: string | undefined;
};

export const XlsxImportDropper = ({ onLoad, onFileLoaded, className }: XlsxImportDropperProps) => {
  const { t } = useTranslation();

  const [dragOver, setDragOver] = useState<boolean>(false);

  const handleDropAsync = useCallback(
    async (file: File) => {
      onLoad(true);
      const data: ArrayBuffer = await file.arrayBuffer();
      const workbook: WorkBook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // TODO: parse header columns from row columns (if no header-row is given in xlsx file)
      const rows = utils.sheet_to_json(worksheet, { header: 1 }) as string[][];
      const headers = rows.shift() ?? [];

      // Fill empty fields in rows with null
      const filledRows = rows.map((row) => {
        const filledRow = Array.from(row, (_, i) => {
          if (!(i in row)) return null;
          else return row[i];
        });
        if (row.length < headers.length) {
          filledRow.push(null);
        }
        return filledRow;
      });

      const fileImport: XlsxFileImport = {
        title: workbook.SheetNames[0] ?? '',
        headers,
        rows: filledRows,
      };

      onFileLoaded(fileImport);
      onLoad(false);
    },
    [onLoad, onFileLoaded],
  );

  function handleFileSelection(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleDropAsync(file);
  }

  useEffect(() => {
    const element = document.getElementById('dropper')!;
    if (!element) throw new Error('Element with id #dropper not found.');

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (e.dataTransfer && e.dataTransfer.files[0]) handleDropAsync(e.dataTransfer.files[0]);
    };

    const handleWindowDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const targetId = (e.target as HTMLElement)?.id;
      if (targetId !== 'dropper') {
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'none';
          e.dataTransfer.dropEffect = 'none';
        }
      }
      setDragOver(false);
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Add event listeners to the dropper element
    element.addEventListener('drop', handleDrop);
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('dragover', handleDragOver);

    // Add global event listeners to prevent default browser behavior
    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('dragover', handleWindowDragOver);

    return () => {
      element.removeEventListener('drop', handleDrop);
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleWindowDrop);
      window.removeEventListener('dragover', handleWindowDragOver);
    };
  }, [handleDropAsync]);

  const classes = classNames(
    styles['xlsx-import-dropper'],
    {
      [styles['drag-over']]: dragOver,
    },
    className,
  );

  return (
    <label id='dropper' className={classes}>
      <div className={styles.wrapper}>
        <ImportIcon className={styles.icon} />
        <div className={styles.text}>{t('import.dropToImport')}</div>
        <input type='file' accept='.xlsx' onChange={(e) => handleFileSelection(e)} />
      </div>
    </label>
  );
};
